import { useState, useCallback, useRef } from 'react'
import { useTaskContext } from 'contexts/task'
import {
  acceptTask as acceptTaskAPI,
  cancelTask as cancelTaskAPI,
  type GetTasksQuery,
  requestTaskCancel as requestTaskCancelAPI,
  revokeTaskCancelRequest as revokeTaskCancelRequestAPI,
  getTasks
} from 'api/task'
import { Task } from 'types/task'

const PAGE_SIZE = 50
type WorkerTaskFilters = Pick<GetTasksQuery, 'category' | 'stockMode'>
const EMPTY_CATEGORY_COUNTS = {
  available: {
    assembly: 0,
    other: 0
  },
  outOfStock: {
    assembly: 0,
    other: 0
  }
}

export const useTask = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { fetchMyTask } = useTaskContext()
  const [tasks, setTasks] = useState<Task[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [categoryCounts, setCategoryCounts] = useState(EMPTY_CATEGORY_COUNTS)
  const [categoryCountsReady, setCategoryCountsReady] = useState(false)
  /** Committed server-side search (API keyword); ref avoids stale closures in loadMore */
  const keywordRef = useRef('')
  const filterRef = useRef<WorkerTaskFilters>({})
  const requestSeqRef = useRef(0)

  const loadFirstPage = useCallback(async (filters?: WorkerTaskFilters) => {
    if (filters) filterRef.current = filters
    const requestSeq = requestSeqRef.current + 1
    requestSeqRef.current = requestSeq
    const kw = keywordRef.current.trim()
    setIsLoading(true)
    try {
      const result = await getTasks({
        page: 1,
        pageSize: PAGE_SIZE,
        keyword: kw || undefined,
        ...filterRef.current
      })
      if (requestSeq !== requestSeqRef.current) return
      const firstBatch = result.data.tasks || []
      setTasks(firstBatch)
      setCategoryCounts(result.data.categoryCounts || EMPTY_CATEGORY_COUNTS)
      setCategoryCountsReady(Boolean(result.data.categoryCounts))
      setPage(1)
      setHasMore(Boolean(result.data.hasMore) && firstBatch.length >= PAGE_SIZE)
    } catch (err) {
      console.error('❌ Error loading tasks', err)
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setIsLoading(false)
      }
    }
  }, [])

  const fetchTasks = useCallback(async (filters?: WorkerTaskFilters) => {
    await loadFirstPage(filters)
  }, [loadFirstPage])

  const commitSearchKeyword = useCallback(
    async (raw: string) => {
      keywordRef.current = raw.trim()
      await loadFirstPage()
    },
    [loadFirstPage]
  )

  const clearSearchKeyword = useCallback(async () => {
    keywordRef.current = ''
    await loadFirstPage()
  }, [loadFirstPage])

  const loadMoreTasks = useCallback(async () => {
    if (!hasMore || isLoadingMore || isLoading) return
    const requestSeq = requestSeqRef.current
    setIsLoadingMore(true)
    try {
      const next = page + 1
      const kw = keywordRef.current.trim()
      const result = await getTasks({
        page: next,
        pageSize: PAGE_SIZE,
        keyword: kw || undefined,
        ...filterRef.current
      })
      if (requestSeq !== requestSeqRef.current) return
      const batch = result.data.tasks || []
      if (result.data.categoryCounts) {
        setCategoryCounts(result.data.categoryCounts)
        setCategoryCountsReady(true)
      }
      if (batch.length === 0) {
        setHasMore(false)
        return
      }
      setTasks(prev => [...prev, ...batch])
      setPage(next)
      setHasMore(
        Boolean(result.data.hasMore) && batch.length >= PAGE_SIZE
      )
    } catch (err) {
      console.error('❌ Error loading more tasks', err)
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setIsLoadingMore(false)
      }
    }
  }, [hasMore, isLoadingMore, isLoading, page])

  const acceptTask = async (taskID: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await acceptTaskAPI(taskID)

      if (res?.data.success && res?.data.task) {
        return true
      } else {
        setError(res?.data.errorCode || 'UNKNOWN_ERROR')
        return false
      }
    } catch (err: any) {
      const code = err?.response?.data?.errorCode || 'UNKNOWN_ERROR'
      setError(code)
      console.error('❌ Accept task failed:', err)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const cancelMyTask = async (taskID: string) => {
    try {
      await cancelTaskAPI(taskID)
      await fetchTasks()
      console.log('Task cancelled and tasks refreshed')
    } catch (error) {
      console.error('❌ Failed to cancel task:', error)
      throw error
    }
  }

  const requestCancel = async (taskID: string) => {
    setError(null)
    try {
      const res = await requestTaskCancelAPI(taskID)
      const updatedTask = res?.data?.task as Partial<Task> | undefined

      setTasks(prev => {
        const target = prev.find(task => task.taskID === taskID)
        if (!target) return prev

        const remaining = prev.filter(task => task.taskID !== taskID)
        return [
          ...remaining,
          {
            ...target,
            ...updatedTask,
            cancelRequestDecision: null,
            cancelRequestedAt:
              updatedTask?.cancelRequestedAt ?? new Date().toISOString()
          }
        ]
      })
    } catch (err: any) {
      const code = err?.response?.data?.errorCode || 'UNKNOWN_ERROR'
      setError(code)
      throw err
    }
  }

  const revokeCancelRequest = async (taskID: string) => {
    setError(null)
    try {
      await revokeTaskCancelRequestAPI(taskID)
      await fetchTasks()
    } catch (err: any) {
      const code = err?.response?.data?.errorCode || 'UNKNOWN_ERROR'
      setError(code)
      throw err
    }
  }

  return {
    acceptTask,
    cancelMyTask,
    requestCancel,
    revokeCancelRequest,
    isLoading,
    isLoadingMore,
    error,
    tasks,
    categoryCounts,
    categoryCountsReady,
    fetchTasks,
    loadMoreTasks,
    commitSearchKeyword,
    clearSearchKeyword,
    hasMore,
    fetchMyTask,
    setError
  }
}
